import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Account,
  Currency,
  Pagination,
  PermissionEnum,
  Response,
  SelectItemProps,
} from '@ui/src';
import {
  MessageType,
  Employee,
  Message,
  Document,
  TopUpInstructions,
} from '../../types';
import { messagesListApi } from '../services';

export interface MessageState {
  messages?: Message[];
  messagesPagination?: Pagination;
  type?: MessageType;
  employees?: Employee[];
  messageText?: string;
  searchText?: string;
  documents?: Document[];
  accounts?: Account[];
  topUpOptions?: {
    companyName: string;
    id: string;
    visibleToClients: boolean;
  }[];
  baseCurrency?: Currency;
  currencies?: SelectItemProps[];
  topUpInstructions?: TopUpInstructions;
}

const initialState: MessageState = {
  searchText: '',
};

export const messageSlice = createSlice({
  name: 'message',
  initialState,
  reducers: {
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    setMessages: (state, action: PayloadAction<Message[]>) => {
      state.messages = action.payload;
    },
    setDocuments: (state, action: PayloadAction<Document[]>) => {
      state.documents = action.payload;
    },
    clearTopUpInstructions: (state) => {
      state.topUpInstructions = undefined;
    },
    clearMessageState: (state) => {
      state.messages = undefined;
      state.type = undefined;
      state.documents = undefined;
      state.accounts = undefined;
      state.topUpOptions = undefined;
      state.topUpInstructions = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      messagesListApi.endpoints.getMessagingEmployeesList.matchFulfilled,
      (state, action: PayloadAction<Response<Employee>>) => {
        const formattedEmployees = action.payload.data.edges
          .filter((item) => item.node.status === 'ACTIVE')
          .map((item) => item.node)
          .map((item) => ({
            ...item,
            permissions: item?.employeePermissions[0]?.permissions || [],
          }))
          .filter(
            (item) =>
              item?.permissions?.includes(PermissionEnum.MESSAGES_LIST) ||
              !item.permissions.length,
          );

        state.employees = formattedEmployees;
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getMessagingTopUp.matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{
            id: string;
            companyName: string;
            visibleToClients: boolean;
          }>
        >,
      ) => {
        state.topUpOptions = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getMessagingTopUp.matchFulfilled,
      (
        state,
        action: PayloadAction<
          Response<{
            id: string;
            companyName: string;
            visibleToClients: boolean;
          }>
        >,
      ) => {
        state.topUpOptions = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getMessages.matchFulfilled,
      (state, action: any & PayloadAction<Response<Message>>) => {
        const messages = action?.payload?.data?.edges?.map(
          (item: { node: Message }) => item.node,
        );

        const sortedMessages = messages?.sort(
          (a: Message, b: Message) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );

        if (!action?.meta?.arg?.originalArgs?.cursor) {
          state.messages = sortedMessages;
        } else {
          const nextMessages = ([] as Message[]).concat(
            state.messages as Message[],
          );

          action.payload.data.edges.forEach((item: { node: Message }) => {
            nextMessages.unshift(item.node);
          });

          state.messages = nextMessages;
        }

        state.messagesPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getChannelDocuments.matchFulfilled,
      (state, action: PayloadAction<Response<Document>>) => {
        state.documents = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getUserAccounts.matchFulfilled,
      (state, action: PayloadAction<Response<Account>>) => {
        state.accounts = action.payload.data.edges.map((item) => item.node);
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getMessages.matchRejected,
      (state, action: any) => {
        if (action?.payload?.status !== 'FETCH_ERROR') {
          state.messages = undefined;
          state.messagesPagination = undefined;
        }
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getTopUpCurrencies.matchFulfilled,
      (
        state,
        action: PayloadAction<{
          currencies: Response<{
            name: string;
            description: string;
            id: string;
          }>;
          baseCurrency: { data: Currency };
        }>,
      ) => {
        const { currencies, baseCurrency } = action.payload;

        if (baseCurrency) {
          state.baseCurrency = baseCurrency.data;
        }

        if (currencies) {
          state.currencies = currencies.data.edges.map((item) => ({
            name: item.node.description,
            value: item.node.id,
            suffix: item.node.name,
          }));
        }
      },
    );
    builder.addMatcher(
      messagesListApi.endpoints.getMessagingTopUpInstructions.matchFulfilled,
      (state, action: PayloadAction<{ data: TopUpInstructions }>) => {
        state.topUpInstructions = action.payload.data;
      },
    );
  },
});

export const {
  clearMessageState,
  setSearchText,
  setMessages,
  setDocuments,
  clearTopUpInstructions,
} = messageSlice.actions;

export default messageSlice.reducer;
